import React, { useState, useLayoutEffect } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Divider,
  Popconfirm,
  Space,
  Input,
  Select,
} from "antd";
import {
  MailOutlined,
  UserOutlined,
  WhatsAppOutlined,
  FileTextOutlined,
  PlusOutlined,
  AppstoreOutlined,
  EditOutlined,
  FilePdfOutlined,
  HomeOutlined,
  SwapOutlined,
  RollbackOutlined,
  CheckOutlined,
  CloseOutlined,
  LoginOutlined,
  QrcodeOutlined,
  DollarOutlined,
  LogoutOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  capitalizeWords,
  getPayTypeLabel,
  getRoomCapicityLabel,
  getRoomTypeLabel,
  getSegmentLabel,
  getServiceLabel,
  PAYMENT_METHOD,
} from "../../constants/constent";
import { displayMessage, getAPI, postAPI } from "../../utils/common";
import {
  BOOKING_ACTION,
  BOOKING_ADV_PAY,
  BOOKING_ADV_PAY_VERIFY,
  BOOKING_ASSIGN_UNASSIGN,
  BOOKING_CHECKIN,
  BOOKING_INVOICE,
  BOOKING_QR_PASS,
  BOOKING_ROOMS,
  IMAGE_BASE_URL,
  WHATSAPP_SEND_BOOKING_MESSAGE,
  WHATSAPP_MESSAGE_TYPES,
  WHATSAPP_SEND_MANUAL_MESSAGE,
} from "../../constants/api";
import {
  ERROR_MSG_TYPE,
  SUCCESS_MESSAGE,
  SUCCESS_MSG_TYPE,
} from "../../constants/messages";
import BookingOrderDrawer from "./BookingOrderDrawer";
import CheckoutModal from "./CheckoutModal";
import { useNavigate } from "react-router-dom";
import BookingEditModal from "./BookingEditModal";
import BookingLogsModal from "./BookingLogsModal";
import ExtraServiceModal from "./extra_service/ExtraServiceModal";
import BookingAppliedServiceModal from "./BookingAppliedServiceModal";
import LS_SERVICE from "../../utils/localStorage";
import AdvancePaymentModal from "./AdvancePaymentModal";
import CancelBookingModal from "./CancelBookingModal";

const BookingPopOver = ({ visible, onClose, booking }) => {
  // Hooks should be called at the top of the component
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groupedRooms, setGroupedRooms] = useState({}); // To store rooms grouped by category
  const [roomSelectionVisible, setRoomSelectionVisible] = useState(false); // State for room selection modal
  // Booking Cancel
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const {
    id,
    customer_name,
    room_category_title,
    user,
    room,
    order_id,
    multi_book_id,
    room_id,
    room_type,
    total_days,
    checkin_date,
    checkout_date,
    booking_segment,
    booking_source,
    payment_method,
    total_with_gst,
    bill_amount,
    booking_status,
    payment_status,
    advance_amount,
    order_amount,
    ex_service_amount,
    rate_plans,
    refunds,
  } = booking;

  const formattedCheckIn = moment(checkin_date).format("D MMM YY");
  const formattedCheckOut = moment(checkout_date).format("D MMM YY");

  // Latest refund (if any) for a compact summary in the popover.
  const latestRefund =
    refunds && refunds.length > 0 ? refunds[refunds.length - 1] : null;

  const getData = () => {
    setLoading(true);
    const successFn = function (data) {
      if (data.status === true) {
        groupRoomsByCategory(data["data"]["rooms"]);
        setLoading(false);
      } else {
        displayMessage(ERROR_MSG_TYPE, data.message);
        setLoading(false);
      }
    };
    const errorFn = function () {
      setLoading(false);
    };
    postAPI(
      BOOKING_ROOMS,
      {
        start_date: formattedCheckIn,
        end_date: formattedCheckOut,
      },
      successFn,
      errorFn,
    );
  };

  const groupRoomsByCategory = (rooms) => {
    const grouped = rooms.reduce((acc, room) => {
      const category = room.room_category?.title || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(room, ...room.similar_rooms);
      return acc;
    }, {});
    setGroupedRooms(grouped);
  };

  useLayoutEffect(() => {
    getData();
  }, [formattedCheckIn, formattedCheckOut]);

  const assignUnassign = async (type, room_id = null) => {
    try {
      setLoading(true);
      const successFn = function (data) {
        if (data.status === true) {
          setLoading(false);
          onClose();
          displayMessage(SUCCESS_MSG_TYPE, data.message);
        } else {
          displayMessage(ERROR_MSG_TYPE, data.message);
          setLoading(false);
        }
      };
      const errorFn = function () {
        setLoading(false);
      };

      postAPI(
        BOOKING_ASSIGN_UNASSIGN,
        {
          booking_id: id,
          type: type,
          room_id: room_id,
        },
        successFn,
        errorFn,
      );
    } catch (err) {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Failed to assignUnassign" + err);
    }
  };

  // Handle Room Assignment
  const handleAssign = (roomId) => {
    assignUnassign("assign", roomId);
    setRoomSelectionVisible(false); // Close room selection modal
  };

  // Handle Room Unassignment
  const handleUnassign = () => {
    assignUnassign("unassign"); // Unassign the room by passing null
  };

  const bookingAction = (actionType) => {
    setLoading(true);
    let body = {
      id: id,
      action_type: actionType,
      reason: cancelReason ?? "",
      cancel_by: LS_SERVICE.get("user_type") ?? "",
    };

    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);
        onClose();
        if (actionType === "reject") {
          setIsCancelModalVisible(false);
          setCancelReason("");
        }
        displayMessage(SUCCESS_MESSAGE, data.message);
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to action");
    };
    postAPI(BOOKING_ACTION, body, successFn, errorFn);
  };

  const checkin = () => {
    setLoading(true);
    let body = {
      booking_id: id,
    };
    console.log(body);
    const successFn = function (data) {
      setLoading(false);
      displayMessage(
        data.status ? SUCCESS_MESSAGE : ERROR_MSG_TYPE,
        data.message,
      );
      onClose();
    };
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to Check-In");
    };
    postAPI(BOOKING_CHECKIN, body, successFn, errorFn);
  };

  // WhatsApp module enabled state (from system settings stored in localStorage).
  const whatsappEnabled = !!(LS_SERVICE.get("setting") || {}).enable_whatsapp;
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  // Manually send the WhatsApp message for this booking. The backend picks the
  // message type from booking_status and enforces sending-enabled + balance.
  const sendWhatsappMessage = () => {
    setSendingWhatsapp(true);
    const successFn = function (data) {
      setSendingWhatsapp(false);
      displayMessage(
        data.status ? SUCCESS_MESSAGE : ERROR_MSG_TYPE,
        data.message,
      );
    };
    const errorFn = function () {
      setSendingWhatsapp(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to send WhatsApp message");
    };
    postAPI(
      WHATSAPP_SEND_BOOKING_MESSAGE,
      { booking_id: id },
      successFn,
      errorFn,
    );
  };

  // Manual message: user chooses which message type to send.
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [messageTypes, setMessageTypes] = useState([]);
  const [selectedMessageType, setSelectedMessageType] = useState(null);

  const openManualModal = () => {
    setSelectedMessageType(null);
    setManualModalVisible(true);
    // Load the available message types on demand.
    getAPI(
      WHATSAPP_MESSAGE_TYPES,
      (data) => {
        if (data.status === true) {
          setMessageTypes(data.data || []);
        } else {
          displayMessage(
            ERROR_MSG_TYPE,
            data.message || "Unable to load message types",
          );
        }
      },
      () => displayMessage(ERROR_MSG_TYPE, "Unable to load message types"),
    );
  };

  const sendManualMessage = () => {
    if (!selectedMessageType) {
      displayMessage(ERROR_MSG_TYPE, "Please select a message to send");
      return;
    }
    setSendingWhatsapp(true);
    const successFn = function (data) {
      setSendingWhatsapp(false);
      displayMessage(
        data.status ? SUCCESS_MESSAGE : ERROR_MSG_TYPE,
        data.message,
      );
      if (data.status) {
        setManualModalVisible(false);
      }
    };
    const errorFn = function () {
      setSendingWhatsapp(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to send WhatsApp message");
    };
    postAPI(
      WHATSAPP_SEND_MANUAL_MESSAGE,
      { booking_id: id, message_type: selectedMessageType },
      successFn,
      errorFn,
    );
  };

  // Booking Checkout
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleClose = () => {
    setEditBookingVisible(false);
    setIsModalVisible(false);
    setLogBookingVisible(false);
    setIsServiceModal(false);
    onClose();
    getData();
  };
  // Booking Checkout
  const openModel = () => {
    setIsModalVisible(true);
  };

  // Booking Order  View
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  // Booking Detail View
  const openOrderDrawer = () => {
    setIsOrderDrawerOpen(true);
  };

  const closeOrderDrawer = () => {
    setIsOrderDrawerOpen(false);
  };

  // Advance Amount
  const [advModalVisible, setAdvModalVisible] = useState(false);
  const [forceCheckIn, setForceCheckIn] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  const handleModalOpen = (force_check_in) => {
    setAdvModalVisible(true);
    setForceCheckIn(force_check_in);
  };

  const handleModalClose = () => {
    setAdvModalVisible(false);
    setForceCheckIn(false);
  };

  const handleAdvancePaySubmit = () => {
    if (parseFloat(advanceAmount) <= 0) {
      displayMessage(
        ERROR_MSG_TYPE,
        "Please enter a valid amount for advance payment",
      );
      return;
    }
    adv_pay_init(id, advanceAmount);
    handleModalClose(); // Close the modal after submitting
  };

  const adv_pay_init = (id, advAmount) => {
    setLoading(true);
    let body = {
      id: id,
      total_amount: advAmount,
      payment_method: paymentMethod,
      force_check_in: forceCheckIn ? "yes" : "no",
    };
    const successFn = function (data) {
      if (data.status === true) {
        setLoading(false);

        if (data.payment_method === "razorpay") {
          const options = {
            key: data.options.key,
            amount: data.options.amount * 100,
            currency: data.options.currency,
            name: data.options.name,
            description: data.options.description,
            image: IMAGE_BASE_URL + data.options.image,
            order_id: data.options.order_id,
            prefill: {
              name: data.options.prefill.name,
              email: data.options.prefill.email,
              contact: data.options.prefill.contact,
            },
            handler: function (response) {
              let body = {
                ...response,
                booking_id: id,
                total_amount: advAmount,
                force_check_in: data.force_check_in,
              };
              console.log(body);
              const successFn = function (data) {
                displayMessage(SUCCESS_MESSAGE, data.message);
                setAdvanceAmount("");
                onClose();
              };
              const errorFn = function () {
                setLoading(false);
                displayMessage(
                  ERROR_MSG_TYPE,
                  "Unable to do payment verification",
                );
              };
              postAPI(BOOKING_ADV_PAY_VERIFY, body, successFn, errorFn);
            },
            theme: {
              color: "#F37254",
            },
          };
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          displayMessage(SUCCESS_MESSAGE, data.message);
          setAdvanceAmount("");
          onClose();
        }
      } else {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };

    // Error callback
    const errorFn = function () {
      setLoading(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to do payment");
    };

    // Make API call to your backend to initiate Razorpay
    postAPI(BOOKING_ADV_PAY, body, successFn, errorFn);
  };

  // Edit Booking Modal
  const [editBookingVisible, setEditBookingVisible] = useState(false);

  // Booking Logs Modal
  const [logBookingVisible, setLogBookingVisible] = useState(false);

  // Booking Extra Service Modal
  const [isServiceModal, setIsServiceModal] = useState(false);

  // Booking Extra Services Modal
  const [isServicesModal, setIsServicesModal] = useState(false);
  return (
    <>
      {/* Main Modal */}
      <Modal
        title={null}
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
      >
        <Row>
          {/* LEFT: Guest Info */}
          <Col
            span={17}
            style={{
              background: "var(--primary-color)",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: "var(--primary-color)",
                color: "#fff",
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div>
                  <UserOutlined /> <b>{customer_name + " - " + id}</b>
                </div>
                <div>
                  <MailOutlined /> {user.email}
                </div>
              </div>
              <Divider style={{ borderColor: "#ccc", margin: "10px 0" }} />
              <Row gutter={16}>
                <Col span={12}>
                  <p>
                    <b>Check-in</b> : {formattedCheckIn}
                  </p>
                  <p>
                    <b>Segment</b> :{" "}
                    {capitalizeWords(
                      getSegmentLabel(booking_segment) +
                        " (" +
                        booking_source.toUpperCase() +
                        ")",
                    )}
                  </p>
                  <p>
                    <b>Room</b> : {room_category_title} (
                    {getRoomTypeLabel(rate_plans?.room_type) || "-"})
                  </p>

                  <p>
                    <b>No. of guest</b> : {getRoomCapicityLabel(room_type)}
                  </p>
                  <p>
                    <b>No. of Nights</b> : {total_days}
                  </p>
                  <p>
                    <b>Payment Mode</b> :{" "}
                    {getPayTypeLabel(payment_method) || "NA"}
                  </p>
                  <p>
                    <b>Advance Amount</b> : {advance_amount || "NA"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <b>Check-out</b> : {formattedCheckOut}
                  </p>
                  <p>
                    <b>Booking-ID</b> : {order_id || "-"}
                  </p>
                  <p>
                    <b>Multi-Booking-ID</b> : {multi_book_id || "-"}
                  </p>
                  <p>
                    <b>Room Service</b> :{" "}
                    {getServiceLabel(rate_plans?.room_service) || "-"}
                  </p>
                  {latestRefund && (
                    <p>
                      <b>Refund</b> : {parseFloat(latestRefund.amount ?? 0).toFixed(2)}{" "}
                      ({latestRefund.status})
                    </p>
                  )}
                </Col>
              </Row>
            </div>
            <div
              style={{
                background: "#003f5c",
                color: "#fff",
                padding: "8px 16px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <b>Amount</b> :{" "}
                {parseFloat(total_with_gst) +
                  parseFloat(order_amount ?? 0) +
                  parseFloat(ex_service_amount ?? 0)}
              </div>
              <div>
                <b>Balance</b> : {parseFloat(bill_amount).toFixed(2) ?? 0}
              </div>
            </div>
            {refunds && refunds.length > 0 && (
              <div
                style={{
                  background: "#0b2e3f",
                  color: "#fff",
                  padding: "8px 16px",
                  fontSize: 13,
                }}
              >
                {refunds.map((refund, index) => (
                  <div
                    key={refund.id ?? index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      padding: "2px 0",
                    }}
                  >
                    <span>
                      <RollbackOutlined />{" "}
                      <b>Refund:</b> {parseFloat(refund.amount ?? 0).toFixed(2)}{" "}
                      {refund.refund_method
                        ? "(" + refund.refund_method + ")"
                        : ""}
                    </span>
                    <span>
                      {refund.razorpay_refund_id
                        ? refund.razorpay_refund_id + " · "
                        : ""}
                      <b>{refund.status}</b>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Col>

          {/* RIGHT: Action Buttons */}
          <Col span={7} style={{ background: "#ffffff", padding: 10 }}>
            {booking_status !== "3" ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Button
                  block
                  icon={<FileTextOutlined />}
                  onClick={() => setLogBookingVisible(true)}
                >
                  Logs
                </Button>

                <Button
                  block
                  icon={<WhatsAppOutlined />}
                  loading={sendingWhatsapp}
                  disabled={!whatsappEnabled || sendingWhatsapp}
                  onClick={sendWhatsappMessage}
                  style={{ backgroundColor: "#25D366", color: "#fff" }}
                >
                  Auto WhatsApp (by Status)
                </Button>

                <Button
                  block
                  icon={<WhatsAppOutlined />}
                  disabled={!whatsappEnabled || sendingWhatsapp}
                  onClick={openManualModal}
                  style={{ borderColor: "#25D366", color: "#128C7E" }}
                >
                  Send WhatsApp Message
                </Button>

                {booking_status !== "2" ? (
                  <Button
                    block
                    icon={<PlusOutlined />}
                    onClick={() => setIsServiceModal(true)}
                  >
                    Add Service
                  </Button>
                ) : null}
                {booking_status !== "2" ? (
                  <Button
                    block
                    icon={<AppstoreOutlined />}
                    onClick={() => setIsServicesModal(true)}
                  >
                    Services
                  </Button>
                ) : null}

                {booking_status !== "3" ? (
                  <Button
                    block
                    icon={<EditOutlined />}
                    onClick={() => setEditBookingVisible(true)}
                  >
                    Edit Booking
                  </Button>
                ) : null}
                {booking_status === "2" && payment_status === "1" ? (
                  <>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={(e) => {
                        const encodedId = btoa(id.toString()); // Convert the ID to Base64
                        const url = `${BOOKING_INVOICE}${encodedId}`;
                        window.open(url, "_blank"); // Open the URL in a new tab or window
                      }}
                      loading={loading}
                      disabled={loading}
                      block
                    >
                      INVOICE
                    </Button>
                  </>
                ) : null}
                {booking_status !== "2" ? (
                  <Button
                    block
                    icon={room_id ? <SwapOutlined /> : <HomeOutlined />}
                    onClick={() => setRoomSelectionVisible(true)}
                  >
                    {!room_id ? `Assign` : `Change Room`}
                  </Button>
                ) : null}
                {booking_status !== "2" && room_id ? (
                  <Button
                    block
                    icon={<RollbackOutlined />}
                    onClick={() => handleUnassign()}
                  >
                    Unassign
                  </Button>
                ) : null}

                {booking_status === "0" ? (
                  <Button
                    block
                    icon={<CheckOutlined />}
                    onClick={() => bookingAction("accept")}
                  >
                    Accept
                  </Button>
                ) : null}
                {booking_status === "0" || booking_status === "4" ? (
                  <Button
                    loading={loading}
                    disabled={loading}
                    danger
                    block
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setIsCancelModalVisible(true);
                    }}
                  >
                    Cancel
                  </Button>
                ) : null}
                {booking_status === "4" ? (
                  <Button
                    block
                    icon={<LoginOutlined />}
                    onClick={(e) =>
                      advance_amount === null ||
                      parseInt(advance_amount) === 0.0
                        ? handleModalOpen(true)
                        : checkin()
                    }
                  >
                    Check-In
                  </Button>
                ) : null}
                <Button
                  block
                  icon={<QrcodeOutlined />}
                  onClick={(e) => {
                    const encodedId = btoa(id.toString());
                    const url = `${BOOKING_QR_PASS}${encodedId}`;
                    window.open(url, "_blank");
                  }}
                >
                  QR Pass
                </Button>
                {booking_status !== "0" &&
                booking_status !== "3" &&
                booking_status !== "2" ? (
                  <Button
                    block
                    icon={<DollarOutlined />}
                    onClick={(e) => handleModalOpen(false)}
                  >
                    Advance AMT
                  </Button>
                ) : null}
                {booking_status === "1" ? (
                  <Popconfirm
                    title="Checkout the booking"
                    description="Are you sure to checkout?"
                    onConfirm={() => openModel()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      loading={loading}
                      disabled={loading}
                      block
                      icon={<LogoutOutlined />}
                    >
                      Checkout
                    </Button>
                  </Popconfirm>
                ) : null}
                {booking_status === "1" ? (
                  <>
                    <Button
                      loading={loading}
                      disabled={loading}
                      onClick={openOrderDrawer}
                      danger
                      icon={<ShoppingOutlined />}
                    >
                      Orders
                    </Button>
                    <Button
                      onClick={(e) => navigate("/order/product/" + id)}
                      loading={loading}
                      disabled={loading}
                      block
                      icon={<ShoppingCartOutlined />}
                    >
                      Take Order
                    </Button>
                  </>
                ) : null}
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <Button
                  block
                  icon={<FileTextOutlined />}
                  onClick={() => setLogBookingVisible(true)}
                >
                  Logs
                </Button>
                <Button
                  block
                  icon={<WhatsAppOutlined />}
                  loading={sendingWhatsapp}
                  disabled={!whatsappEnabled || sendingWhatsapp}
                  onClick={sendWhatsappMessage}
                  style={{ backgroundColor: "#25D366", color: "#fff" }}
                >
                  Auto WhatsApp (by Status)
                </Button>

                <Button
                  block
                  icon={<WhatsAppOutlined />}
                  disabled={!whatsappEnabled || sendingWhatsapp}
                  onClick={openManualModal}
                  style={{ borderColor: "#25D366", color: "#128C7E" }}
                >
                  Send WhatsApp Message
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Modal>

      {/* Send WhatsApp Message (manual type selection) Modal */}
      <Modal
        title="Send WhatsApp Message"
        open={manualModalVisible}
        onCancel={() => !sendingWhatsapp && setManualModalVisible(false)}
        onOk={sendManualMessage}
        okText={sendingWhatsapp ? "Sending..." : "Send"}
        confirmLoading={sendingWhatsapp}
        okButtonProps={{ disabled: sendingWhatsapp || !selectedMessageType }}
        cancelButtonProps={{ disabled: sendingWhatsapp }}
        maskClosable={!sendingWhatsapp}
      >
        <p>Select which WhatsApp message you want to send for this booking:</p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select a message"
          value={selectedMessageType}
          onChange={(val) => setSelectedMessageType(val)}
          options={messageTypes}
        />
      </Modal>

      {/* Room Selection Modal for Assigning Room */}
      <Modal
        title=""
        visible={roomSelectionVisible}
        onCancel={() => setRoomSelectionVisible(false)}
        footer={null}
        width={500}
      >
        {Object.keys(groupedRooms).map((category) => (
          <div key={category}>
            <h3>
              <b>{category}</b>
            </h3>
            <Row gutter={16}>
              {groupedRooms[category].map((room) => (
                <Col span={12} key={room.id}>
                  <Button
                    block
                    onClick={() => handleAssign(room.id)}
                    disabled={loading}
                  >
                    {room.name} ({room.room_no})
                  </Button>
                </Col>
              ))}
            </Row>
            <Divider />
          </div>
        ))}
      </Modal>

      {/* Cancel Booking Modal (reusable: asks full refund vs policy) */}
      {isCancelModalVisible && booking && (
        <CancelBookingModal
          visible={isCancelModalVisible}
          booking={booking}
          onClose={() => setIsCancelModalVisible(false)}
          onConfirmed={() => {
            setIsCancelModalVisible(false);
            onClose();
          }}
        />
      )}

      {/* Extra Service Modal */}
      {isServiceModal && id && (
        <ExtraServiceModal
          visible={isServiceModal}
          onClose={handleClose}
          booking_id={id}
        />
      )}

      {/* Booking Extra Service Modal */}
      {isServicesModal && id && (
        <BookingAppliedServiceModal
          visible={isServicesModal}
          onClose={() => {
            setIsServicesModal(false);
          }}
          booking_id={id}
        />
      )}

      {/* Checkout Modal */}
      {isModalVisible && booking && (
        <CheckoutModal
          visible={isModalVisible}
          onClose={handleClose}
          booking={booking} // Pass the booking data here
        />
      )}

      {/* Booking Order View */}
      {isOrderDrawerOpen && booking && (
        <BookingOrderDrawer
          open={isOrderDrawerOpen}
          onClose={closeOrderDrawer}
          booking={booking}
        />
      )}

      {/* Booking Logs Modal */}
      {logBookingVisible && id && (
        <BookingLogsModal
          visible={logBookingVisible}
          onClose={() => {
            setLogBookingVisible(false);
          }}
          booking_id={id}
        />
      )}

      {/* Booking Edit Modal */}
      {editBookingVisible && booking && (
        <BookingEditModal
          visible={editBookingVisible}
          onClose={handleClose}
          booking={booking}
        />
      )}

      {/* Advance Amount Modal */}
      {advModalVisible && (
        <AdvancePaymentModal
          visible={advModalVisible}
          onCancel={() => setAdvModalVisible(false)}
          onSubmit={handleAdvancePaySubmit}
          loading={loading}
          amount={advanceAmount}
          setAmount={setAdvanceAmount}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          PAYMENT_METHOD={PAYMENT_METHOD}
        />
      )}
    </>
  );
};

export default BookingPopOver;
